import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { useToast } from "../ui/use-toast";
import { useSearchParams } from "react-router-dom";
import React from "react";
import { useUTMTracking } from "../../hooks/useUTMTracking";
import { RTLInput, RTLTextarea } from "../ui/rtl-input";
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from "../../utils/rtl";
import { ContentLabels } from "../../types/navigation";

// Form step types
type FormStep = 
  | "welcome" 
  | "basic-info"
  | "purpose"
  | "investment"
  | "end-user"
  | "contact"
  | "success";

// Country code type
interface CountryCode {
  code: string;
  country: string;
  flag: string;
  popular?: boolean;
}

// Form data schema
const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.object({
    countryCode: z.string(),
    number: z.string().min(6, "Phone number is too short").max(15, "Phone number is too long")
  }).optional(),
  purpose: z.enum(["investment", "end-user"]),
  budget: z.string().optional(),
  timeline: z.string().optional(),
  bedrooms: z.string().optional(),
  contactPreference: z.enum(["whatsapp", "phone", "email"]).optional()
});

type FormData = z.infer<typeof formSchema>;

// Animation variants
const formVariants = {
  hidden: { 
    opacity: 0,
    x: -20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0,
    x: 20,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};

// All country codes data
const allCountryCodes: CountryCode[] = [
  { code: "+971", country: "United Arab Emirates", flag: "🇦🇪", popular: true },
  { code: "+91", country: "India", flag: "🇮🇳", popular: true },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧", popular: true },
  { code: "+1", country: "United States", flag: "🇺🇸", popular: true },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦", popular: true },
  { code: "+974", country: "Qatar", flag: "🇶🇦", popular: true },
  { code: "+965", country: "Kuwait", flag: "🇰🇼", popular: true },
  { code: "+968", country: "Oman", flag: "🇴🇲", popular: true },
  { code: "+973", country: "Bahrain", flag: "🇧🇭", popular: true },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+962", country: "Jordan", flag: "🇯🇴" },
  { code: "+961", country: "Lebanon", flag: "🇱🇧" },
  { code: "+963", country: "Syria", flag: "🇸🇾" },
  { code: "+964", country: "Iraq", flag: "🇮🇶" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+90", country: "Turkey", flag: "🇹🇷" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+93", country: "Afghanistan", flag: "🇦🇫" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+852", country: "Hong Kong", flag: "🇭🇰" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+358", country: "Finland", flag: "🇫🇮" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+353", country: "Ireland", flag: "🇮🇪" },
  { code: "+972", country: "Israel", flag: "🇮🇱" }
];

interface DynamicLeadFormProps {
  projectId: Id<"projects">;
  language: string;
  contentLabels?: ContentLabels;
  content?: {
    title?: string;
    subtitle?: string;
    welcomeTitle?: string;
    welcomeDescription?: string;
    benefits?: string[];
    getStartedText?: string;
    takesSecondsText?: string;
    
    // Step titles and descriptions
    stepTitles?: {
      basicInfo?: string;
      purpose?: string;
      investment?: string;
      endUser?: string;
      contact?: string;
    };
    
    stepDescriptions?: {
      basicInfo?: string;
      purpose?: string;
      investment?: string;
      endUser?: string;
      contact?: string;
    };
    
    // Purpose option descriptions
    purposeDescriptions?: {
      investment?: string;
      endUser?: string;
    };
    
    // Contact option descriptions
    contactDescriptions?: {
      whatsapp?: string;
      phone?: string;
      email?: string;
    };
    
    // Form labels
    labels?: {
      fullName?: string;
      email?: string;
      phone?: string;
      whatsYourPurpose?: string;
      investment?: string;
      endUser?: string;
      investmentPreferences?: string;
      investmentBudget?: string;
      investmentTimeline?: string;
      yourDreamHome?: string;
      preferredSize?: string;
      budgetRange?: string;
      howToContact?: string;
      whatsapp?: string;
      phoneCall?: string;
      emailOption?: string;
      continue?: string;
      back?: string;
      submit?: string;
      submitting?: string;
      thankYou?: string;
      willContactYou?: string;
    };
    
    // Options
    budgetOptions?: { value: string; label: string }[];
    timelineOptions?: { value: string; label: string }[];
    bedroomOptions?: { value: string; label: string }[];
    
    // Validation messages
    validationMessages?: {
      nameRequired?: string;
      emailRequired?: string;
      emailInvalid?: string;
      phoneRequired?: string;
      phoneInvalid?: string;
    };
    
    success?: {
      title?: string;
      description?: string;
    };
    error?: {
      title?: string;
      description?: string;
    };
  };
}

export const DynamicLeadForm: React.FC<DynamicLeadFormProps> = ({ projectId, language, contentLabels, content }) => {
  const [currentStep, setCurrentStep] = useState<FormStep>("welcome");
  const [formData, setFormData] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm<FormData>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const createLead = useMutation(api.leads.create);
  const linkLeadToSession = useMutation(api.visitorTracking.linkLeadToSession);
  
  // RTL detection
  const isCurrentLanguageRTL = isRTL(language);
  const textDirection = getTextDirection(language);
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);
  
  // Use UTM tracking hook
  const { getLeadTrackingData } = useUTMTracking(projectId);
  
  // Anti-spam states
  const [formStartTime] = useState<number>(Date.now());
  const [honeypot, setHoneypot] = useState<string>('');
  
  // Basic Info step states
  const [selectedCountry, setSelectedCountry] = useState("+971");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // UTM tracking is now handled by the useUTMTracking hook above

  // Filter countries based on search - memoized
  const filteredCountries = useMemo(() => 
    allCountryCodes.filter(country => 
      country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery)
    ), [searchQuery]
  );

  // Group countries by popular and others - memoized
  const { popularCountries, otherCountries } = useMemo(() => ({
    popularCountries: filteredCountries.filter(c => c.popular),
    otherCountries: filteredCountries.filter(c => !c.popular)
  }), [filteredCountries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format phone number
  const formatPhoneNumber = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
  }, []);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    e.target.value = formatted;
  }, [formatPhoneNumber]);

  // Country selection handlers
  const handleCountrySelect = useCallback((countryCode: string) => {
    setSelectedCountry(countryCode);
    setIsCountryDropdownOpen(false);
    setSearchQuery("");
  }, []);

  const handleCountrySearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchQuery(e.target.value);
  }, []);

  // Memoized country search input to prevent focus loss
  const CountrySearchInput = React.memo(() => (
    <RTLInput
      type="text"
      language={language}
      placeholder={contentLabels?.searchCountries || "Search countries..."}
      value={searchQuery}
      onChange={handleCountrySearch}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
    />
  ));

  // Memoized country button to prevent re-renders
  const CountryButton = React.memo(({ country, isSelected, onClick }: {
    country: CountryCode;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm rounded-md ${
        isSelected
          ? 'bg-primary/5 text-primary'
          : 'hover:bg-gray-50'
      }`}
    >
      <span className="text-lg">{country.flag}</span>
      <span className="flex-1 text-sm">{country.country}</span>
      <span className="text-gray-500 text-xs">{country.code}</span>
    </button>
  ));

  const handleBasicInfoSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const name = watch('name');
    const email = watch('email');
    const phoneNumber = watch('phone')?.number;
    
    if (name && name.length >= 2 && 
        email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        phoneNumber && phoneNumber.length >= 6) {
      setFormData(prev => ({ 
        ...prev, 
        name, 
        email, 
        phone: {
          countryCode: selectedCountry,
          number: phoneNumber
        }
      }));
      setCurrentStep("purpose");
    }
  }, [watch, selectedCountry]);

  // Progress calculation
  const getProgress = () => {
    const steps: FormStep[] = ["welcome", "basic-info", "purpose"];
    if (formData.purpose === "investment") {
      steps.push("investment", "contact");
    } else if (formData.purpose === "end-user") {
      steps.push("end-user", "contact");
    }
    return ((steps.indexOf(currentStep) + 1) / steps.length) * 100;
  };

  // Default content - memoized
  const defaultContent = useMemo(() => ({
    title: content?.title || contentLabels?.getPersonalizedDetails || "Get Personalized Property Details",
    subtitle: content?.subtitle || contentLabels?.speakWithExperts || "Speak with our property experts and get:",
    welcomeTitle: content?.welcomeTitle || contentLabels?.getPersonalizedDetails || "Get Personalized Property Details",
    welcomeDescription: content?.welcomeDescription || contentLabels?.speakWithExperts || "Speak with our property experts and get:",
    benefits: content?.benefits || [
      contentLabels?.benefit1 || "Detailed floor plans & pricing",
      contentLabels?.benefit2 || "Virtual tour of the property", 
      contentLabels?.benefit3 || "Payment plan options",
      contentLabels?.benefit4 || "Investment analysis report"
    ].filter(Boolean),
    getStartedText: content?.getStartedText || contentLabels?.getStartedNow || "Get Started Now",
    takesSecondsText: content?.takesSecondsText || contentLabels?.takesOnlySeconds || "Takes only a few seconds",
    
    // Step titles
    stepTitles: {
      basicInfo: content?.stepTitles?.basicInfo || contentLabels?.letsGetToKnowYou || "Let's get to know you",
      purpose: content?.stepTitles?.purpose || contentLabels?.whatBringsYouHere || "What brings you here today?",
      investment: content?.stepTitles?.investment || contentLabels?.investmentPreferences || "Investment Preferences",
      endUser: content?.stepTitles?.endUser || contentLabels?.yourDreamHome || "Your Dream Home",
      contact: content?.stepTitles?.contact || contentLabels?.howToContactYou || "How would you like us to contact you?"
    },
    
    // Step descriptions
    stepDescriptions: {
      basicInfo: content?.stepDescriptions?.basicInfo || contentLabels?.enterDetailsForPersonalized || "Enter your details to receive personalized information",
      purpose: content?.stepDescriptions?.purpose || contentLabels?.selectPrimaryInterest || "Select your primary interest",
      investment: content?.stepDescriptions?.investment || contentLabels?.tellUsInvestmentPreferences || "Tell us about your investment preferences",
      endUser: content?.stepDescriptions?.endUser || contentLabels?.tellUsIdealHome || "Tell us about your ideal home",
      contact: content?.stepDescriptions?.contact || contentLabels?.choosePreferredContact || "Choose your preferred contact method"
    },
    
    // Purpose descriptions
    purposeDescriptions: {
      investment: content?.purposeDescriptions?.investment || contentLabels?.lookingForInvestment || "Looking for a profitable investment opportunity in Dubai's growing real estate market",
      endUser: content?.purposeDescriptions?.endUser || contentLabels?.searchingForHome || "Searching for your perfect home in Dubai to live in"
    },
    
    // Contact option descriptions
    contactDescriptions: {
      whatsapp: content?.contactDescriptions?.whatsapp || contentLabels?.getInstantUpdates || "Get instant updates via WhatsApp",
      phone: content?.contactDescriptions?.phone || contentLabels?.speakDirectlyExperts || "Speak directly with our experts",
      email: content?.contactDescriptions?.email || contentLabels?.receiveDetailedEmail || "Receive detailed information via email"
    },
    
    labels: {
      fullName: content?.labels?.fullName || contentLabels?.fullName || "Full Name",
      email: content?.labels?.email || contentLabels?.email || "Email Address",
      phone: content?.labels?.phone || contentLabels?.phoneNumber || "Phone Number",
      whatsYourPurpose: content?.labels?.whatsYourPurpose || contentLabels?.whatBringsYouHere || "What brings you here today?",
      investment: content?.labels?.investment || contentLabels?.investment || "Investment",
      endUser: content?.labels?.endUser || contentLabels?.endUser || "End User",
      investmentPreferences: content?.labels?.investmentPreferences || contentLabels?.investmentPreferences || "Investment Preferences",
      investmentBudget: content?.labels?.investmentBudget || contentLabels?.investmentBudget || "Investment Budget",
      investmentTimeline: content?.labels?.investmentTimeline || contentLabels?.investmentTimeline || "Investment Timeline",
      yourDreamHome: content?.labels?.yourDreamHome || contentLabels?.yourDreamHome || "Your Dream Home",
      preferredSize: content?.labels?.preferredSize || contentLabels?.preferredSize || "Preferred Size",
      budgetRange: content?.labels?.budgetRange || contentLabels?.budgetRange || "Budget Range",
      howToContact: content?.labels?.howToContact || contentLabels?.howToContactYou || "How would you like us to contact you?",
      whatsapp: content?.labels?.whatsapp || contentLabels?.whatsappContact || "WhatsApp",
      phoneCall: content?.labels?.phoneCall || contentLabels?.phoneCall || "Phone Call",
      emailOption: content?.labels?.emailOption || contentLabels?.emailOption || "Email",
      continue: content?.labels?.continue || contentLabels?.continue || "Continue",
      back: content?.labels?.back || contentLabels?.back || "Back",
      submit: content?.labels?.submit || contentLabels?.submit || "Submit",
      submitting: content?.labels?.submitting || contentLabels?.submitting || "Submitting...",
      thankYou: content?.labels?.thankYou || contentLabels?.thankYou || "Thank You!",
      willContactYou: content?.labels?.willContactYou || contentLabels?.willContactYou || "Our team will contact you shortly via your preferred method.",
      ...content?.labels
    },
    
    // Validation messages
    validationMessages: {
      nameRequired: content?.validationMessages?.nameRequired || contentLabels?.nameRequired || "Please enter your full name",
      emailRequired: content?.validationMessages?.emailRequired || contentLabels?.emailRequired || "Please enter your email address",
      emailInvalid: content?.validationMessages?.emailInvalid || contentLabels?.emailInvalid || "Please enter a valid email address",
      phoneRequired: content?.validationMessages?.phoneRequired || contentLabels?.phoneRequired || "Please enter your phone number",
      phoneInvalid: content?.validationMessages?.phoneInvalid || "Please enter a valid phone number"
    },
    
    budgetOptions: content?.budgetOptions || [
      { value: "500k-1m", label: "AED 500K - 1M" },
      { value: "1m-2m", label: "AED 1M - 2M" },
      { value: "2m-5m", label: "AED 2M - 5M" },
      { value: "5m+", label: "AED 5M+" }
    ],
    timelineOptions: content?.timelineOptions || [
      { value: "immediate", label: "Immediate" },
      { value: "3months", label: "Within 3 months" },
      { value: "6months", label: "Within 6 months" },
      { value: "1year", label: "Within 1 year" }
    ],
    bedroomOptions: content?.bedroomOptions || [
      { value: "studio", label: "Studio" },
      { value: "1bed", label: "1 Bedroom" },
      { value: "2bed", label: "2 Bedrooms" },
      { value: "3bed", label: "3 Bedrooms" },
      { value: "4bed+", label: "4+ Bedrooms" }
    ],
    success: {
      title: content?.success?.title || "Lead Submission Successful",
      description: content?.success?.description || "Thank you for your interest. We'll review your submission and get back to you soon."
    },
    error: {
      title: content?.error?.title || "Submission Error",
      description: content?.error?.description || "Failed to submit the lead. Please try again later."
    }
  }), [content, contentLabels]);

  // Welcome screen component - no useCallback to prevent unnecessary re-renders
  const WelcomeScreen = () => (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="text-center space-y-6"
    >
      <div className="space-y-4">
        <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full text-primary">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold text-gray-900">
          {defaultContent.welcomeTitle}
        </h3>
        <p className="text-gray-600 text-lg max-w-md mx-auto">
          {defaultContent.welcomeDescription}
        </p>
        <ul className="space-y-3 text-left max-w-md mx-auto mt-6">
          {defaultContent.benefits.map((benefit) => (
            <li
              key={benefit}
              className="flex items-center space-x-3 text-gray-700"
            >
              <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="pt-6">
        <motion.button
          onClick={() => setCurrentStep("basic-info")}
          className="px-8 py-4 bg-primary text-white rounded-lg shadow-lg hover:bg-primary/90 transition-colors text-lg font-semibold w-full sm:w-auto"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {defaultContent.getStartedText}
        </motion.button>
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500 space-x-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{defaultContent.takesSecondsText}</span>
        </div>
      </div>
    </motion.div>
  );

  // Progress bar component
  const ProgressBar = () => (
    <motion.div 
      className="h-1 bg-gray-200 rounded-full overflow-hidden"
      initial={false}
    >
      <motion.div
        className="h-full bg-primary"
        initial={{ width: 0 }}
        animate={{ width: `${getProgress()}%` }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  );

  // Basic Info step component - NO useCallback to prevent focus loss
  const BasicInfoStep = () => (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-8"
    >
      <div className="text-center space-y-3">
        <h3 className="text-2xl font-bold text-gray-900">
          {defaultContent.stepTitles.basicInfo}
        </h3>
        <p className="text-gray-600">
          {defaultContent.stepDescriptions.basicInfo}
        </p>
      </div>

      <form 
        onSubmit={handleBasicInfoSubmit}
        className="space-y-6"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label 
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              {defaultContent.labels.fullName}
            </label>
            <RTLInput
              type="text"
              id="name"
              language={language}
              {...register("name", { required: true, minLength: 2 })}
              className={`mt-1 block w-full px-4 py-3 rounded-lg border ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
              placeholder={`Enter your ${defaultContent.labels.fullName.toLowerCase()}`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">
                {defaultContent.validationMessages.nameRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label 
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              {defaultContent.labels.email}
            </label>
            <RTLInput
              type="email"
              id="email"
              language={language}
              {...register("email", { 
                required: true, 
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              })}
              className={`mt-1 block w-full px-4 py-3 rounded-lg border ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
              placeholder={`Enter your ${defaultContent.labels.email.toLowerCase()}`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {defaultContent.validationMessages.emailInvalid}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label 
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              {defaultContent.labels.phone}
            </label>
            <div className="flex gap-2">
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                  className="flex items-center gap-2 w-[110px] px-2 py-3 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
                >
                  <span className="text-lg">
                    {allCountryCodes.find(c => c.code === selectedCountry)?.flag}
                  </span>
                  <span className="flex-1 text-left text-sm font-medium truncate">
                    {selectedCountry}
                  </span>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <AnimatePresence>
                  {isCountryDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 mt-1 w-[260px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="p-2">
                        <CountrySearchInput />
                      </div>

                      <div className="max-h-[280px] overflow-y-auto overscroll-contain">
                        {popularCountries.length > 0 && (
                          <div className="px-2 py-1.5">
                            <div className="text-xs font-medium text-gray-500 px-2 mb-1">Popular</div>
                            {popularCountries.map((country) => (
                              <CountryButton
                                key={country.code}
                                country={country}
                                isSelected={selectedCountry === country.code}
                                onClick={() => handleCountrySelect(country.code)}
                              />
                            ))}
                          </div>
                        )}

                        {popularCountries.length > 0 && otherCountries.length > 0 && (
                          <div className="h-px bg-gray-200 mx-2 my-1" />
                        )}

                        {otherCountries.length > 0 && (
                          <div className="px-2 py-1.5">
                            {popularCountries.length > 0 && (
                              <div className="text-xs font-medium text-gray-500 px-2 mb-1">Other Countries</div>
                            )}
                            {otherCountries.map((country) => (
                              <CountryButton
                                key={country.code}
                                country={country}
                                isSelected={selectedCountry === country.code}
                                onClick={() => handleCountrySelect(country.code)}
                              />
                            ))}
                          </div>
                        )}

                        {filteredCountries.length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No countries found
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <RTLInput
                type="tel"
                id="phone"
                language={language}
                {...register("phone.number", { 
                  required: true,
                  minLength: 6,
                  maxLength: 15,
                  pattern: /^[0-9\s]+$/
                })}
                onChange={handlePhoneChange}
                className={`min-w-0 w-full max-w-[180px] sm:max-w-none px-4 py-3 rounded-lg border ${
                  errors.phone?.number ? 'border-red-500' : 'border-gray-300'
                } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
                placeholder="Enter phone number"
              />
            </div>
            {errors.phone?.number && (
              <p className="mt-1 text-sm text-red-600">
                {defaultContent.validationMessages.phoneInvalid}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Format: Country Code + Phone Number (e.g., +971 55 123 4567)
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          <motion.button
            type="button"
            onClick={() => setCurrentStep("welcome")}
            className="text-gray-600 hover:text-gray-800 transition-colors flex items-center space-x-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>{defaultContent.labels.back}</span>
          </motion.button>
          <motion.button
            type="submit"
            className="px-6 py-3 bg-primary text-white rounded-lg shadow-md hover:bg-primary/90 transition-colors font-medium"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {defaultContent.labels.continue}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );

  // Purpose Selection step component
  const PurposeStep = () => {
    const handlePurposeSelect = (purpose: "investment" | "end-user") => {
      // Clear fields that are specific to the other purpose when switching
      const clearedFormData = { ...formData, purpose };
      
      if (purpose === "investment") {
        // When switching to investment, clear end-user specific fields
        delete clearedFormData.bedrooms;
      } else if (purpose === "end-user") {
        // When switching to end-user, clear investment specific fields
        delete clearedFormData.timeline;
      }
      
      setFormData(clearedFormData);
      setCurrentStep(purpose === "investment" ? "investment" : "end-user");
    };

    return (
      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="space-y-8"
      >
        <div className="text-center">
          <h3 className="text-2xl font-semibold mb-4">
            {defaultContent.stepTitles.purpose}
          </h3>
          <p className="text-gray-600">
            {defaultContent.stepDescriptions.purpose}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => handlePurposeSelect("investment")}
            className="w-full h-48 bg-white rounded-xl shadow-lg p-6 text-left hover:shadow-xl transition-shadow relative overflow-hidden group"
          >
            <div className="relative z-10">
              <span className="text-primary">
                <svg className="w-8 h-8 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <h4 className="text-xl font-semibold mb-2">{defaultContent.labels.investment}</h4>
              <p className="text-gray-600 text-sm">{defaultContent.purposeDescriptions.investment}</p>
            </div>
            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          <button
            onClick={() => handlePurposeSelect("end-user")}
            className="w-full h-48 bg-white rounded-xl shadow-lg p-6 text-left hover:shadow-xl transition-shadow relative overflow-hidden group"
          >
            <div className="relative z-10">
              <span className="text-primary">
                <svg className="w-8 h-8 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </span>
              <h4 className="text-xl font-semibold mb-2">{defaultContent.labels.endUser}</h4>
              <p className="text-gray-600 text-sm">{defaultContent.purposeDescriptions.endUser}</p>
            </div>
            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>

        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep("basic-info")}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            {defaultContent.labels.back}
          </button>
        </div>
      </motion.div>
    );
  };

  // Investment preferences step
  const InvestmentStep = () => {
    const handleBudgetSelect = (budget: string) => {
      setFormData({ ...formData, budget });
    };

    const handleTimelineSelect = (timeline: string) => {
      setFormData({ ...formData, timeline });
    };

    const handleContinue = () => {
      if (formData.budget && formData.timeline) {
        setCurrentStep("contact");
      }
    };

    return (
      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="space-y-8"
      >
        <div className="text-center">
          <h3 className="text-2xl font-semibold mb-4">
            {defaultContent.stepTitles.investment}
          </h3>
          <p className="text-gray-600">
            {defaultContent.stepDescriptions.investment}
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.investmentBudget}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {defaultContent.budgetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleBudgetSelect(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    formData.budget === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.investmentTimeline}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {defaultContent.timelineOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTimelineSelect(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    formData.timeline === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep("purpose")}
            className="text-gray-600 hover:text-gray-800 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>{defaultContent.labels.back}</span>
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!formData.budget || !formData.timeline}
            className={`px-6 py-3 rounded-lg shadow-md font-medium transition-colors ${
              formData.budget && formData.timeline
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {defaultContent.labels.continue}
          </button>
        </div>
      </motion.div>
    );
  };

  // End user preferences step
  const EndUserStep = () => {
    const handleBedroomsSelect = (bedrooms: string) => {
      setFormData({ ...formData, bedrooms });
    };

    const handleBudgetSelect = (budget: string) => {
      setFormData({ ...formData, budget });
    };

    const handleContinue = () => {
      if (formData.bedrooms && formData.budget) {
        setCurrentStep("contact");
      }
    };

    return (
      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="space-y-8"
      >
        <div className="text-center">
          <h3 className="text-2xl font-semibold mb-4">
            {defaultContent.stepTitles.endUser}
          </h3>
          <p className="text-gray-600">
            {defaultContent.stepDescriptions.endUser}
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.preferredSize}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {defaultContent.bedroomOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleBedroomsSelect(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    formData.bedrooms === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.budgetRange}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {defaultContent.budgetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleBudgetSelect(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    formData.budget === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep("purpose")}
            className="text-gray-600 hover:text-gray-800 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>{defaultContent.labels.back}</span>
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!formData.bedrooms || !formData.budget}
            className={`px-6 py-3 rounded-lg shadow-md font-medium transition-colors ${
              formData.bedrooms && formData.budget
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {defaultContent.labels.continue}
          </button>
        </div>
      </motion.div>
    );
  };

  // Contact preferences step
  const ContactStep = () => {
    const handleContactPreferenceSelect = (preference: "whatsapp" | "phone" | "email") => {
      setFormData({ ...formData, contactPreference: preference });
      
      // GTM Tracking - Contact Method Selection
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': `${preference}_click`,
          'contact_method': preference,
          'property_id': projectId,
          'click_location': 'lead_form_contact_step',
          'lead_value': preference === 'phone' ? 150 : preference === 'whatsapp' ? 120 : 80,
          'currency': 'AED'
        });
      }
    };

    const handleSubmit = async () => {
      if (!formData.contactPreference || isSubmitting) return;

      // Validate that all required data is available
      if (!formData.name || !formData.email || !formData.phone || !formData.purpose) {
        toast({
          title: "Missing Information",
          description: "Please complete all required fields before submitting.",
          variant: "destructive"
        });
        return;
      }

      setIsSubmitting(true);

      // GTM Tracking - Lead Generation Event
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'generate_lead',
          'lead_type': 'lead_form',
          'contact_method': formData.contactPreference,
          'property_id': projectId,
          'form_type': 'dynamic_lead_form',
          'lead_value': 100,
          'currency': 'AED',
          'user_email': formData.email,
          'user_phone': formData.phone?.number,
          'lead_source': 'website',
          'page_location': window.location.href,
          'user_purpose': formData.purpose,
          'user_budget': formData.budget,
          'user_timeline': formData.timeline,
          'user_bedrooms': formData.bedrooms
        });
      }

      try {
    
        
        // Get user's IP address
        const ipAddress = await fetch('https://api.ipify.org?format=json')
          .then(res => res.json())
          .then(data => data.ip)
          .catch(() => null);

        // Submit lead to database
        const result = await createLead({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          projectId,
          language,
          purpose: formData.purpose,
          budget: formData.budget,
          timeline: formData.timeline,
          bedrooms: formData.bedrooms,
          contactPreference: formData.contactPreference,
          formType: 'lead_form',
          ...getLeadTrackingData(),
          ipAddress: ipAddress || undefined,
          userAgent: navigator.userAgent,
          // Anti-spam fields
          honeypot: honeypot,
          formStartTime: formStartTime,
          formSubmitTime: Date.now(),
        });



        if (result.success) {
          // Link the lead to the visitor session if we have tracking data
          const trackingData = getLeadTrackingData();
          if (trackingData.visitorId && trackingData.sessionId) {
            try {
              await linkLeadToSession({
                sessionId: trackingData.sessionId,
                visitorId: trackingData.visitorId,
                leadId: result.leadId,
              });
            } catch (error) {
      
            }
          }
          
          toast({
            title: defaultContent.success.title,
            description: defaultContent.success.description,
            variant: "default"
          });
          
          setCurrentStep("success");
          
          // Reset form after delay
          setTimeout(() => {
            reset();
            setFormData({});
            setCurrentStep("welcome");
          }, 3000);
        } else {
          throw new Error("Failed to submit");
        }
      } catch (error) {
  
        toast({
          title: defaultContent.error.title,
          description: defaultContent.error.description,
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="space-y-8"
      >
        <div className="text-center">
          <h3 className="text-2xl font-semibold mb-4">
            {defaultContent.stepTitles.contact}
          </h3>
          <p className="text-gray-600">
            {defaultContent.stepDescriptions.contact}
          </p>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            onClick={() => handleContactPreferenceSelect("whatsapp")}
            className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
              formData.contactPreference === "whatsapp"
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold">{defaultContent.labels.whatsapp}</h4>
                <p className="text-sm text-gray-600">{defaultContent.contactDescriptions.whatsapp}</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleContactPreferenceSelect("phone")}
            className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
              formData.contactPreference === "phone"
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold">{defaultContent.labels.phoneCall}</h4>
                <p className="text-sm text-gray-600">{defaultContent.contactDescriptions.phone}</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleContactPreferenceSelect("email")}
            className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
              formData.contactPreference === "email"
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold">{defaultContent.labels.emailOption}</h4>
                <p className="text-sm text-gray-600">{defaultContent.contactDescriptions.email}</p>
              </div>
            </div>
          </button>
        </div>

        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep(formData.purpose === "investment" ? "investment" : "end-user")}
            className="text-gray-600 hover:text-gray-800 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>{defaultContent.labels.back}</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!formData.contactPreference || isSubmitting}
            className={`px-6 py-3 rounded-lg shadow-md font-medium transition-colors ${
              formData.contactPreference && !isSubmitting
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? defaultContent.labels.submitting : defaultContent.labels.submit}
          </button>
        </div>
      </motion.div>
    );
  };

  // Success step component
  const SuccessStep = () => (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="text-center py-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="w-16 h-16 mx-auto mb-6 bg-green-100 text-green-500 rounded-full flex items-center justify-center"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </motion.div>
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-semibold mb-4"
      >
        {defaultContent.labels.thankYou}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-gray-600 mb-4"
      >
        {defaultContent.labels.willContactYou}
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4"
      >
        <div className="flex items-center justify-center mb-2">
          <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-blue-800 font-medium">Welcome Email Sent!</span>
        </div>
        <p className="text-blue-700 text-sm">
          Check your email inbox for a welcome message with next steps and additional information.
        </p>
      </motion.div>
    </motion.div>
  );

  return (
    <section className={`py-16 md:py-24 bg-gray-50 ${rtlClasses}`} dir={dirAttribute}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Honeypot field */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none'
              }}
              tabIndex={-1}
              autoComplete="off"
            />
            
            {currentStep !== "welcome" && (
              <div className="mb-8">
                <ProgressBar />
              </div>
            )}
            
            <AnimatePresence mode="wait">
              {currentStep === "welcome" && <WelcomeScreen />}
              {currentStep === "basic-info" && <BasicInfoStep />}
              {currentStep === "purpose" && <PurposeStep />}
              {currentStep === "investment" && <InvestmentStep />}
              {currentStep === "end-user" && <EndUserStep />}
              {currentStep === "contact" && <ContactStep />}
              {currentStep === "success" && <SuccessStep />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}; 